@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.enginechat

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.example.project.dashboard.DashboardTheme
import org.example.project.dashboard.MobileDashboardBackHandler
import org.example.project.manager.NodeScope
import org.example.project.service.GlobalManager
import org.example.project.service.LoginService
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.settings.LumaticaCheckbox
import org.example.project.utils.All
import org.example.project.utils.LocalizationManager
import org.example.project.utils.UserAvatarCircle
import org.example.project.settings.UISettings
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.attributes.value
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.events.Event

private enum class MobileChatView {
    Rooms,
    Contacts,
    Conversation
}

private const val MOBILE_CHAT_MESSAGES_ID = "mobile-chat-messages"

private val mobileChatGermanTexts = mapOf(
    "mobile_chat_title" to "Chat",
    "mobile_chat_loading" to "Chats werden geladen ...",
    "mobile_chat_start_failed" to "Chat konnte nicht gestartet werden.",
    "mobile_chat_group_start_failed" to "Gruppenchat konnte nicht gestartet werden.",
    "mobile_chat_not_found" to "Chat wurde nicht gefunden.",
    "mobile_chat_no_messages" to "Noch keine Nachrichten vorhanden.",
    "mobile_chat_message_placeholder" to "Nachricht",
    "mobile_chat_no_contacts" to "Keine Kontakte gefunden.",
    "mobile_chat_group_chat" to "Gruppenchat",
    "mobile_chat_start" to "Chat starten",
    "mobile_chat_no_open_chats" to "Noch keine offenen Chats.",
    "mobile_chat_open" to "Chat öffnen",
    "mobile_chat_info" to "Info",
    "mobile_chat_delete" to "Chat löschen",
    "mobile_chat_you" to "Du"
)

private fun mobileChatText(art: String, key: String): String {
    if (art == NodeScope.NET) return LocalizationManager.get(key)
    return mobileChatGermanTexts[key] ?: key
}

private fun mobileChatSystemText(key: String): String {
    val translated = LocalizationManager.get(key)
    return translated.takeUnless { it == "[$key]" } ?: mobileChatGermanTexts[key] ?: key
}

private fun mobileChatViewportHeight(): Double {
    val visualViewport = window.asDynamic().visualViewport
    return ((visualViewport?.height as? Number)?.toDouble() ?: window.innerHeight.toDouble())
        .coerceAtLeast(260.0)
}

private fun scrollMobileChatToBottom() {
    window.setTimeout({
        val element = document.getElementById(MOBILE_CHAT_MESSAGES_ID)
        if (element != null) {
            element.scrollTop = element.scrollHeight.toDouble()
        }
    }, 0)
}

@Composable
fun MobileChatLayout(
    art: String = NodeScope.CUS
) {
    Style(MobileChatStyles)
    val t: (String) -> String = { mobileChatText(art, it) }
    val scope = rememberCoroutineScope()
    val currentUser = LoginService.getUser()
    val currentUserId = currentUser?.id.orEmpty().trim()
    val currentUserIds = remember(currentUser?.firmenId, currentUser?.id) { currentUserChatIds() }
    val roomVersion = GlobalManager.jsonTopicVersion("room.json")
    var view by remember { mutableStateOf(MobileChatView.Rooms) }
    var rooms by remember { mutableStateOf<List<All>>(emptyList()) }
    var contactData by remember { mutableStateOf(ChatContactsData()) }
    var selectedRoomId by remember { mutableStateOf<String?>(null) }
    var messages by remember { mutableStateOf<List<All>>(emptyList()) }
    var inputText by remember { mutableStateOf("") }
    var loading by remember { mutableStateOf(true) }
    var saving by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var viewportHeight by remember { mutableStateOf(mobileChatViewportHeight()) }
    var groupMode by remember { mutableStateOf(false) }
    var selectedGroupContactIds by remember { mutableStateOf<Set<String>>(emptySet()) }
    var menuRoom by remember { mutableStateOf<All?>(null) }
    var infoRoom by remember { mutableStateOf<All?>(null) }
    var longPressTimer by remember { mutableStateOf<Int?>(null) }
    val selectedRoom = rooms.firstOrNull { it.id == selectedRoomId }
    val allUsers = contactData.allUsers

    fun openRoom(room: All) {
        selectedRoomId = room.id
        view = MobileChatView.Conversation
    }

    fun reloadRooms() {
        scope.launch {
            rooms = mergeRoomLists(rooms, loadStandaloneRooms(art, currentUserIds))
        }
    }

    fun sendCurrentMessage() {
        val room = selectedRoom ?: return
        val text = inputText.trim()
        if (text.isBlank() || saving) return
        saving = true
        scope.launch {
            persistMessage(art, room, text).onSuccess {
                inputText = ""
                messages = loadStandaloneMessages(art, room.id)
                reloadRooms()
            }
            saving = false
        }
    }

    fun startChat(user: All) {
        val senderId = currentSenderChatId(art)
        val targetId = user.id.trim()
        if (senderId.isBlank() || targetId.isBlank()) return
        scope.launch {
            ensureStandaloneRoom(art, setOf(senderId, targetId)).onSuccess { room ->
                rooms = mergeRoomLists(rooms, listOf(room))
                openRoom(room)
            }.onFailure {
                error = t("mobile_chat_start_failed")
            }
        }
    }

    fun startGroupChat() {
        val senderId = currentSenderChatId(art)
        val participants = selectedGroupContactIds.map { it.trim() }.filter { it.isNotBlank() }.toSet() + senderId
        if (senderId.isBlank() || participants.size < 2) return
        scope.launch {
            ensureStandaloneRoom(art, participants).onSuccess { room ->
                rooms = mergeRoomLists(rooms, listOf(room))
                selectedGroupContactIds = emptySet()
                groupMode = false
                openRoom(room)
            }.onFailure {
                error = t("mobile_chat_group_start_failed")
            }
        }
    }

    fun closeRoomMenu() {
        menuRoom = null
    }

    fun deleteRoomForCurrentUser(room: All) {
        val senderId = currentSenderChatId(art)
        if (room.id.isBlank() || senderId.isBlank()) return
        scope.launch {
            ChatApi.deleteRoomForParticipant(room.id, senderId)
            rooms = loadStandaloneRooms(art, currentUserIds)
            if (selectedRoomId == room.id) {
                selectedRoomId = null
                view = MobileChatView.Rooms
            }
            if (infoRoom?.id == room.id) infoRoom = null
            closeRoomMenu()
        }
    }

    LaunchedEffect(art, currentUserId) {
        loading = true
        error = null
        val loadedRooms = loadStandaloneRooms(art, currentUserIds)
        val loadedContactData = loadChatContactData(currentUserId)
        contactData = loadedContactData.copy(
            allUsers = loadedContactData.allUsers + loadMissingChatUsers(loadedRooms, loadedContactData.allUsers)
        )
        rooms = loadedRooms
        loading = false
    }

    LaunchedEffect(selectedRoom?.id) {
        messages = selectedRoom?.id?.let {
            val loaded = loadStandaloneMessages(art, it)
            markStandaloneRoomRead(it)
            loaded
        }.orEmpty()
    }

    LaunchedEffect(art, currentUserId, selectedRoomId, roomVersion) {
        if (loading) return@LaunchedEffect
        val freshRooms = loadStandaloneRooms(art, currentUserIds)
        rooms = mergeRoomLists(rooms, freshRooms)
        val missingUsers = loadMissingChatUsers(freshRooms, contactData.allUsers)
        if (missingUsers.isNotEmpty()) {
            contactData = contactData.copy(allUsers = contactData.allUsers + missingUsers)
        }
        val activeRoomId = selectedRoomId
        if (activeRoomId != null) {
            messages = loadStandaloneMessages(art, activeRoomId)
            markStandaloneRoomRead(activeRoomId)
        }
    }

    LaunchedEffect(view, selectedRoom?.id, messages.size, viewportHeight) {
        if (view == MobileChatView.Conversation) {
            scrollMobileChatToBottom()
        }
    }

    DisposableEffect(Unit) {
        val resizeListener: (Event) -> Unit = {
            viewportHeight = mobileChatViewportHeight()
        }
        window.addEventListener("resize", resizeListener)
        val visualViewport = window.asDynamic().visualViewport
        if (visualViewport != null) {
            visualViewport.addEventListener("resize", resizeListener)
            visualViewport.addEventListener("scroll", resizeListener)
        }
        onDispose {
            window.removeEventListener("resize", resizeListener)
            if (visualViewport != null) {
                visualViewport.removeEventListener("resize", resizeListener)
                visualViewport.removeEventListener("scroll", resizeListener)
            }
        }
    }

    DisposableEffect(view) {
        val backHandler = {
            when (view) {
                MobileChatView.Conversation -> {
                    view = MobileChatView.Rooms
                    selectedRoomId = null
                    infoRoom = null
                    closeRoomMenu()
                    true
                }
                MobileChatView.Contacts -> {
                    groupMode = false
                    selectedGroupContactIds = emptySet()
                    closeRoomMenu()
                    view = MobileChatView.Rooms
                    true
                }
                MobileChatView.Rooms -> {
                    if (infoRoom != null || menuRoom != null) {
                        infoRoom = null
                        closeRoomMenu()
                        true
                    } else {
                        false
                    }
                }
            }
        }
        MobileDashboardBackHandler.handler.value = backHandler
        onDispose {
            if (MobileDashboardBackHandler.handler.value === backHandler) {
                MobileDashboardBackHandler.handler.value = null
            }
        }
    }

    Div({
        classes(MobileChatStyles.mobileRoot)
        if (view == MobileChatView.Conversation) {
            style { maxHeight(viewportHeight.px) }
        }
    }) {
        when {
            loading -> Div({ classes(MobileChatStyles.emptyState) }) { Text(t("mobile_chat_loading")) }
            error != null -> Div({ classes(MobileChatStyles.emptyState) }) { Text(error ?: "") }
            view == MobileChatView.Conversation -> {
                Div({ classes(MobileChatStyles.conversation) }) {
                    Div({
                        classes(MobileChatStyles.messageList)
                        id(MOBILE_CHAT_MESSAGES_ID)
                        ref { element ->
                            element.scrollTop = element.scrollHeight.toDouble()
                            onDispose { }
                        }
                    }) {
                        if (selectedRoom == null) {
                            Div({ classes(MobileChatStyles.emptyState) }) { Text(t("mobile_chat_not_found")) }
                        } else if (messages.isEmpty()) {
                            Div({ classes(MobileChatStyles.emptyState) }) { Text(t("mobile_chat_no_messages")) }
                        } else {
                            ChatMessageList(messages, { isOwnMessage(it, currentUserIds) }, { chatAuthorLabel(it, currentUserIds, allUsers) })
                        }
                    }
                    Div({ classes(MobileChatStyles.inputBar) }) {
                        Input(InputType.Text, attrs = {
                            classes(MobileChatStyles.input)
                            value(inputText)
                            placeholder(t("mobile_chat_message_placeholder"))
                            onInput { inputText = it.value }
                            onKeyDown {
                                if ((it.key == "Enter" || it.code == "Enter" || it.code == "NumpadEnter") && inputText.isNotBlank()) {
                                    it.preventDefault()
                                    sendCurrentMessage()
                                }
                            }
                        })
                        Div({
                            style {
                                width(36.px)
                                height(36.px)
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                justifyContent(JustifyContent.Center)
                                cursor(if (inputText.isNotBlank() && !saving) "pointer" else "default")
                                opacity(if (inputText.isNotBlank() && !saving) 0.92 else 0.38)
                                flexShrink(0)
                            }
                            if (inputText.isNotBlank() && !saving) {
                                onClick { sendCurrentMessage() }
                            }
                        }) {
                            Img(src = "images/send.svg", attrs = {
                                style {
                                    width(24.px)
                                    height(24.px)
                                    property("filter", "brightness(0) invert(1)")
                                }
                            })
                        }
                    }
                }
            }
            view == MobileChatView.Contacts -> {
                Div({ classes(MobileChatStyles.list) }) {
                    val contacts = contactData.contactCompanies.flatMap { it.columnSettings }
                    if (contacts.isEmpty()) {
                        Div({ classes(MobileChatStyles.emptyState) }) { Text(t("mobile_chat_no_contacts")) }
                    } else {
                        contacts.forEach { user ->
                            val userId = user.id.trim()
                            MobileContactRow(
                                user = user,
                                groupMode = groupMode,
                                selected = userId in selectedGroupContactIds,
                                onToggle = { checked ->
                                    selectedGroupContactIds = if (checked) {
                                        selectedGroupContactIds + userId
                                    } else {
                                        selectedGroupContactIds - userId
                                    }
                                },
                                onClick = {
                                    if (groupMode) {
                                        selectedGroupContactIds = if (userId in selectedGroupContactIds) {
                                            selectedGroupContactIds - userId
                                        } else {
                                            selectedGroupContactIds + userId
                                        }
                                    } else {
                                        startChat(user)
                                    }
                                }
                            )
                        }
                    }
                }
                Div({ classes(MobileChatStyles.bottomAction, MobileChatStyles.groupActionBar) }) {
                    LumaticaButton(
                        label = t("mobile_chat_group_chat"),
                        onClick = {
                            groupMode = !groupMode
                            if (!groupMode) selectedGroupContactIds = emptySet()
                        },
                        variant = LumaticaButtonVariant.State,
                        isActive = groupMode,
                        heightPx = 36,
                        paddingH = 14
                    )
                    if (groupMode) {
                        LumaticaButton(
                            label = t("mobile_chat_start"),
                            onClick = { startGroupChat() },
                            variant = LumaticaButtonVariant.Action,
                            enabled = selectedGroupContactIds.isNotEmpty(),
                            heightPx = 36,
                            paddingH = 14
                        )
                    }
                }
            }
            else -> {
                Div({ classes(MobileChatStyles.list) }) {
                    if (rooms.isEmpty()) {
                        Div({ classes(MobileChatStyles.emptyState) }) { Text(t("mobile_chat_no_open_chats")) }
                    } else if (infoRoom != null) {
                        MobileChatInfoView(
                            room = infoRoom!!,
                            currentUserIds = currentUserIds,
                            allUsers = allUsers,
                            onDelete = { deleteRoomForCurrentUser(infoRoom!!) }
                        )
                    } else {
                        rooms.forEach { room ->
                            MobileRoomRow(
                                room = room,
                                currentUserIds = currentUserIds,
                                allUsers = allUsers,
                                menuOpen = menuRoom?.id == room.id,
                                onInfo = {
                                    infoRoom = room
                                    closeRoomMenu()
                                },
                                onDelete = { deleteRoomForCurrentUser(room) },
                                onLongPress = {
                                    menuRoom = room
                                },
                                onPressStart = {
                                    longPressTimer?.let { window.clearTimeout(it) }
                                    longPressTimer = window.setTimeout({
                                        menuRoom = room
                                        longPressTimer = null
                                    }, 520)
                                },
                                onPressEnd = {
                                    longPressTimer?.let { window.clearTimeout(it) }
                                    longPressTimer = null
                                },
                                onClick = { openRoom(room) }
                            )
                        }
                    }
                }
                Div({ classes(MobileChatStyles.bottomAction) }) {
                    Img(src = "images/addbox.svg", attrs = {
                        classes(MobileChatStyles.addChatIcon)
                        onClick { view = MobileChatView.Contacts }
                    })
                }
            }
        }
    }
}

@Composable
private fun MobileRoomRow(
    room: All,
    currentUserIds: Set<String>,
    allUsers: Map<String, All>,
    menuOpen: Boolean,
    onInfo: () -> Unit,
    onDelete: () -> Unit,
    onLongPress: () -> Unit,
    onPressStart: () -> Unit,
    onPressEnd: () -> Unit,
    onClick: () -> Unit
) {
    val title = roomTitle(room, currentUserIds, allUsers)
    val avatarUser = roomParticipantIds(room)
        .firstOrNull { it !in currentUserIds }
        ?.let { allUsers[it] }
    Div({
        classes(MobileChatStyles.chatRow)
        onMouseDown { onPressStart() }
        onMouseUp { onPressEnd() }
        onMouseLeave { onPressEnd() }
        onTouchStart { onPressStart() }
        onTouchEnd { onPressEnd() }
        onContextMenu {
            it.preventDefault()
            onLongPress()
        }
        onClick { onClick() }
    }) {
        UserAvatarCircle(user = avatarUser, fallbackLabel = title, sizePx = 42, background = UISettings.SALES_KPI_BG)
        Div({ classes(MobileChatStyles.chatRowText) }) {
            Span({ classes(MobileChatStyles.chatRowName) }) { Text(title) }
            Span({ classes(MobileChatStyles.chatRowLast) }) {
                Text(avatarUser?.let(::userPositionLabel)?.takeIf { it != "-" }.orEmpty().ifBlank {
                    roomLastMessage(room).ifBlank { mobileChatSystemText("mobile_chat_open") }
                })
            }
        }
        Span({ classes(MobileChatStyles.chatRowTime) }) {
            Text(roomLastTime(room).orEmpty())
        }
    }
    if (menuOpen) {
        Div({ classes(MobileChatStyles.chatMenu) }) {
            LumaticaButton(mobileChatSystemText("mobile_chat_info"), onClick = onInfo, heightPx = 32, paddingH = 12)
            LumaticaButton(mobileChatSystemText("mobile_chat_delete"), onClick = onDelete, heightPx = 32, paddingH = 12)
        }
    }
}

@Composable
private fun MobileChatInfoView(
    room: All,
    currentUserIds: Set<String>,
    allUsers: Map<String, All>,
    onDelete: () -> Unit
) {
    val participants = roomParticipantIds(room)
    Div({ classes(MobileChatStyles.infoPanel) }) {
        participants.forEach { participantId ->
            val user = allUsers[participantId]
            Div({ classes(MobileChatStyles.chatRow) }) {
                UserAvatarCircle(user = user, fallbackLabel = user?.let(::userDisplayName) ?: participantId, sizePx = 42, background = UISettings.SALES_KPI_BG)
                Div({ classes(MobileChatStyles.chatRowText) }) {
                    Span({ classes(MobileChatStyles.chatRowName) }) {
                        Text(
                            when {
                                participantId in currentUserIds -> mobileChatSystemText("mobile_chat_you")
                                user != null -> userDisplayName(user)
                                else -> participantId
                            }
                        )
                    }
                    Span({ classes(MobileChatStyles.chatRowLast) }) {
                        Text(user?.let(::userCompanyLabel).orEmpty().ifBlank { "-" })
                    }
                }
            }
        }
        Div({ classes(MobileChatStyles.infoAction) }) {
            LumaticaButton(mobileChatSystemText("mobile_chat_delete"), onClick = onDelete, variant = LumaticaButtonVariant.Action)
        }
    }
}

@Composable
private fun MobileContactRow(
    user: All,
    groupMode: Boolean,
    selected: Boolean,
    onToggle: (Boolean) -> Unit,
    onClick: () -> Unit
) {
    Div({
        classes(if (groupMode) MobileChatStyles.groupContactRow else MobileChatStyles.chatRow)
        onClick { onClick() }
    }) {
        if (groupMode) {
            LumaticaCheckbox(checked = selected, onChange = onToggle)
        }
        UserAvatarCircle(user = user, fallbackLabel = userDisplayName(user), sizePx = 42, background = UISettings.SALES_KPI_BG)
        Div({ classes(MobileChatStyles.chatRowText) }) {
            Span({ classes(MobileChatStyles.chatRowName) }) { Text(userDisplayName(user)) }
            Span({ classes(MobileChatStyles.chatRowLast) }) { Text(userCompanyLabel(user)) }
        }
    }
}

private object MobileChatStyles : StyleSheet() {
    val mobileRoot by style {
        width(100.percent)
        height(100.percent)
        minHeight(0.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        color(DashboardTheme.TEXT_LIGHT)
        backgroundColor(Color.transparent)
        boxSizing("border-box")
    }

    val list by style {
        flex(1)
        minHeight(0.px)
        overflowY("auto")
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(10.px)
        padding(10.px)
        boxSizing("border-box")
    }

    val row by style {
        padding(14.px, 15.px)
        borderRadius(8.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(6.px)
        property("background", "rgba(255,255,255,0.04)")
        border(1.px, LineStyle.Solid, DashboardTheme.BORDER_LIGHT)
        cursor("pointer")
        boxSizing("border-box")
    }

    val chatRow by style {
        display(DisplayStyle.Grid)
        property("grid-template-columns", "46px minmax(0, 1fr) auto")
        alignItems(AlignItems.Center)
        gap(12.px)
        padding(10.px, 2.px)
        property("background", "transparent")
        cursor("pointer")
        boxSizing("border-box")
    }

    val groupContactRow by style {
        display(DisplayStyle.Grid)
        property("grid-template-columns", "28px 46px minmax(0, 1fr)")
        alignItems(AlignItems.Center)
        gap(12.px)
        padding(10.px, 2.px)
        property("background", "transparent")
        cursor("pointer")
        boxSizing("border-box")
    }

    val chatRowText by style {
        minWidth(0.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(4.px)
    }

    val chatRowName by style {
        color(DashboardTheme.TEXT_LIGHT)
        fontSize(15.px)
        fontWeight("850")
        overflow("hidden")
        property("text-overflow", "ellipsis")
        whiteSpace("nowrap")
    }

    val chatRowLast by style {
        color(DashboardTheme.TEXT_MUTED)
        fontSize(13.px)
        overflow("hidden")
        property("text-overflow", "ellipsis")
        whiteSpace("nowrap")
    }

    val chatRowTime by style {
        alignSelf(AlignSelf.Start)
        color(rgba(247, 247, 245, 0.52))
        fontSize(11.px)
        fontWeight("800")
        whiteSpace("nowrap")
        paddingTop(2.px)
    }

    val chatMenu by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.FlexEnd)
        gap(8.px)
        padding(0.px, 2.px, 8.px, 2.px)
        boxSizing("border-box")
    }

    val infoPanel by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(10.px)
        width(100.percent)
        boxSizing("border-box")
    }

    val infoAction by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        paddingTop(8.px)
    }

    val rowTitle by style {
        color(DashboardTheme.TEXT_LIGHT)
        fontSize(15.px)
        fontWeight("800")
        overflow("hidden")
        property("text-overflow", "ellipsis")
        whiteSpace("nowrap")
    }

    val rowBody by style {
        color(DashboardTheme.TEXT_MUTED)
        fontSize(13.px)
        lineHeight("1.35")
        overflow("hidden")
        property("text-overflow", "ellipsis")
        whiteSpace("nowrap")
    }

    val rowMeta by style {
        color(rgba(247, 247, 245, 0.48))
        fontSize(11.px)
        fontWeight("700")
    }

    val bottomAction by style {
        padding(10.px)
        flexShrink(0)
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        property("border-top", "1px solid transparent")
        boxSizing("border-box")
    }

    val groupActionBar by style {
        gap(8.px)
    }

    val addChatIcon by style {
        width(38.px)
        height(38.px)
        cursor("pointer")
    }

    val conversation by style {
        flex(1)
        minHeight(0.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        padding(8.px, 10.px, 10.px, 10.px)
        boxSizing("border-box")
    }

    val messageList by style {
        flex(1)
        minHeight(0.px)
        overflowY("auto")
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(10.px)
        paddingBottom(12.px)
    }

    val inputBar by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(8.px)
        paddingTop(8.px)
        property("border-top", "1px solid rgba(255,255,255,0.10)")
        flexShrink(0)
    }

    val input by style {
        flex(1)
        minWidth(0.px)
        height(36.px)
        padding(0.px, 12.px)
        borderRadius(8.px)
        backgroundColor(Color.transparent)
        color(DashboardTheme.TEXT_LIGHT)
        border(1.px, LineStyle.Solid, DashboardTheme.BORDER_LIGHT)
        boxSizing("border-box")
        fontSize(13.px)
    }

    val emptyState by style {
        flex(1)
        minHeight(120.px)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        textAlign("center")
        color(DashboardTheme.TEXT_MUTED)
        fontSize(14.px)
        padding(18.px)
        boxSizing("border-box")
    }
}
